import { useState } from "react";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Pencil, Trash2, ExternalLink, Calendar as CalIcon } from "lucide-react";

const COLUMNS = [
  { key: "Applied", tint: "#5C6B64", bg: "#F4F1EE" },
  { key: "Interviewing", tint: "#D99C3D", bg: "#FDF2DF" },
  { key: "Offer", tint: "#1A362D", bg: "#E2F0E7" },
  { key: "Rejected", tint: "#C65D47", bg: "#FCECEC" },
];

export default function KanbanBoard({ apps, onEdit, onDelete, onStatusChange }) {
  const [dragOver, setDragOver] = useState(null);

  const handleDrop = async (status, e) => {
    e.preventDefault();
    setDragOver(null);
    const id = e.dataTransfer.getData("text/plain");
    if (id) onStatusChange?.(id, status);
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4" data-testid="kanban-board">
      {COLUMNS.map((col) => {
        const items = apps.filter((a) => a.status === col.key);
        return (
          <div
            key={col.key}
            data-testid={`kanban-col-${col.key}`}
            onDragOver={(e) => { e.preventDefault(); setDragOver(col.key); }}
            onDragLeave={() => setDragOver(null)}
            onDrop={(e) => handleDrop(col.key, e)}
            className={`rounded-2xl border p-4 min-h-[320px] transition-colors ${
              dragOver === col.key ? "border-[#1A362D] bg-white" : "border-[#E5E1DB] bg-white/60"
            }`}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full" style={{ background: col.tint }} />
                <h3 className="font-display text-lg text-[#1A362D]">{col.key}</h3>
              </div>
              <Badge className="rounded-full bg-[#F4F1EE] text-[#5C6B64] border-0">{items.length}</Badge>
            </div>
            <div className="space-y-3">
              {items.length === 0 ? (
                <p className="text-xs text-[#5C6B64] italic">Drag applications here</p>
              ) : items.map((a) => (
                <div
                  key={a.id}
                  draggable
                  onDragStart={(e) => e.dataTransfer.setData("text/plain", a.id)}
                  className="rounded-xl bg-white border border-[#E5E1DB] p-3 cursor-grab active:cursor-grabbing hover:shadow-md hover:shadow-black/5 transition-all"
                  data-testid={`kanban-card-${a.id}`}
                  style={{ borderLeft: `3px solid ${col.tint}` }}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="font-medium text-[#1A362D] truncate">{a.company}</p>
                      <p className="text-sm text-[#5C6B64] truncate">{a.job_title}</p>
                    </div>
                    <div className="flex gap-0.5 shrink-0">
                      <Button variant="ghost" size="icon" className="h-7 w-7 rounded-full"
                        onClick={() => onEdit(a)} data-testid={`kanban-edit-${a.id}`}>
                        <Pencil className="w-3.5 h-3.5" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7 rounded-full text-[#C65D47]"
                        onClick={() => onDelete(a)} data-testid={`kanban-delete-${a.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-1.5 text-xs text-[#5C6B64]">
                    {a.status === "Interviewing" && a.interview_stage && (
                      <span className="rounded-full bg-[#FDF2DF] text-[#8B6A1F] px-2 py-0.5 font-medium">{a.interview_stage}</span>
                    )}
                    {a.pay_range && <span className="rounded-full bg-[#F4F1EE] px-2 py-0.5">{a.pay_range}</span>}
                    {a.website && <span className="rounded-full bg-[#F4F1EE] px-2 py-0.5">{a.website}</span>}
                    {a.interview_date && (
                      <span className="rounded-full bg-[#FDF2DF] text-[#8B6A1F] px-2 py-0.5 inline-flex items-center gap-1">
                        <CalIcon className="w-3 h-3" /> {a.interview_date}
                      </span>
                    )}
                  </div>
                  {a.recruiter_name && (
                    <p className="text-xs text-[#5C6B64] mt-1.5">Recruiter: {a.recruiter_name}</p>
                  )}
                  {a.job_url && (
                    <a href={a.job_url} target="_blank" rel="noreferrer"
                      className="text-xs mt-2 inline-flex items-center gap-1 text-[#1A362D] hover:text-[#C65D47]">
                      Open link <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
